// @flow

import React, { useState, useEffect } from 'react'
import glamorous from 'glamorous'
import { css } from 'glamor'
import getContent from 'pp-react-l10n'
import PPAmountField from 'pp-amount-field/dist/pp-amount-field.basic.v2.umd'
import {
  HeadingText,
  BodyText,
  Link,
  Icon,
  grey100,
  grey200,
  grey600,
  grey700,
  blue600,
} from '@paypalcorp/pp-react'

const l10n = getContent('pages/profile')

const PrimaryText = glamorous.div({
  color: grey700,
})
const SecondaryText = glamorous.div({
  color: grey600,
})

const Card = glamorous.div({
  minHeight: '12.125rem',
  width: '21.5rem',
  borderRadius: '0.75rem',
  backgroundColor: 'white',
  boxShadow: '0 2px 4px 0 rgba(0,0,0,0.15)',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
})

const Content = glamorous.div(({ showActions = false }) => ({
  margin: `1rem 1.5rem ${showActions ? '1.5rem' : '2rem'}`,
  overflowWrap: 'break-word',
}))

const AvatarContainer = glamorous.div({
  height: '5.625rem',
  width: '5.625rem',
  marginTop: '-3.125rem',
  marginLeft: '1.25rem',
  position: 'relative',
})
const AvatarStyles = {
  width: 'inherit',
  height: 'inherit',
  border: '4px solid white',
  borderRadius: '50%',
}
const GenericAvatar = glamorous.div(({ background }) => ({
  ...AvatarStyles,
  background,
  textAlign: 'center',
  ' span': {
    color: 'white',
    lineHeight: '5.125rem',
  },
}))
const FadeIn = css.keyframes({ from: { opacity: 0 }, to: { opacity: 1 } })
const PhotoAvatar = glamorous.div(({ photoUrl, fadeIn = true }) => ({
  ...AvatarStyles,
  backgroundImage: `url("${photoUrl}")`,
  backgroundSize: 'cover',
  backgroundPosition: '50%',
  backgroundRepeat: 'no-repeat',
  ...(fadeIn && { animation: `${FadeIn} 0.2s` }),
}))
const AvatarEdit = glamorous.button({
  color: 'inherit',
  padding: 0,
  font: 'inherit',
  cursor: 'pointer',
  outline: 'inherit',
  width: '2rem',
  height: '2rem',
  border: '2px solid white',
  textAlign: 'center',
  borderRadius: '50%',
  backgroundColor: grey200,
  position: 'absolute',
  bottom: 0,
  right: 0,
  '&:focus': {
    outline: '1px auto rgb(0, 95, 204)',
  },
})

const EditCamera = css({
  color: grey700,
  lineHeight: '1.75rem',
})

const ShareContainer = glamorous.div({
  position: 'absolute',
  top: '0.8125rem',
  right: '0.5rem',
})
const ShareButton = glamorous.button({
  background: 'none',
  border: 'none',
  padding: '0',
  cursor: 'pointer',
  outline: 'inherit',
})

const SecondaryContent = glamorous.div({ marginTop: '0.125rem' })
const TertiaryContent = glamorous.div({ marginTop: '1.5rem' })

const AmountContainer = glamorous.div({
  marginTop: '1rem',
  ' .ppaf-input': {
    '&:focus': {
      outline: 'revert',
    },
  },
  ' .ppaf-select-wrapper': {
    marginTop: '0.375rem',
  },
  ' .ppaf-select': {
    paddingLeft: '0.5rem',
    paddingRight: '0.5rem',
  },
})

const Footer = glamorous.div({
  display: 'flex',
  marginTop: 'auto',
  overflowWrap: 'break-word',
})

const Button = glamorous.a(({ borderRight }) => ({
  borderTop: '1px solid ' + grey100,
  ...(borderRight && { borderRight: '1px solid ' + grey100 }),
  flexBasis: '100%',
  minWidth: 0, // let flex item shrink smaller than content
  padding: '1rem 0',
  textDecoration: 'none',
  '&:active, &:focus, &:hover, &:visited': {
    textDecoration: 'none',
  },
}))

type Props = {
  isEditable?: boolean,
  onEdit?: () => {},
  isShareable?: boolean,
  onShare?: () => {},
  photoUrl?: string,
  displayName: string,
  handle: string,
  displayLocation?: string,
  blurb?: string,
  showActions?: boolean,
  sendLink?: string,
  onSend?: () => any,
  showAmount?: boolean,
  amount?: string,
  currencyCode?: string,
  currencyDecimal?: string,
  currencySymbolMap?: { [string]: string },
  formatAmount?: (amount: string) => string,
  amountLabel?: string,
}

function ProfileCard(props: Props) {
  const {
    isEditable = false,
    onEdit,
    isShareable = false,
    onShare,
    photoUrl,
    displayName,
    handle,
    displayLocation,
    blurb,
    sendLink,
    onSend,
    showActions = false,
    showAmount = false,
    amount,
    currencyCode,
    currencyDecimal,
    currencySymbolMap,
    formatAmount,
    amountLabel,
  } = props

  const [isLoaded, setIsLoaded] = useState(!photoUrl)
  const [isPhotoCached, setIsPhotoCached] = useState(false)

  useEffect(() => {
    if (!photoUrl) {
      return
    }

    const photoImage = new Image()
    photoImage.onload = () => {
      setIsLoaded(true)
    }
    photoImage.onerror = () => {
      // image failed to load so do nothing
    }
    photoImage.src = photoUrl
    if (photoImage.complete) {
      setIsPhotoCached(true)
    }

    return () => {
      photoImage.onload = () => {}
    }
  }, [])

  return (
    <Card>
      <AvatarContainer>
        {photoUrl ? (
          isLoaded && (
            <PhotoAvatar
              photoUrl={photoUrl}
              role="img"
              aria-label={l10n('photo.alt')}
              fadeIn={!isPhotoCached}
            />
          )
        ) : (
          <GenericAvatar
            background={blue600}
            data-test-id="profile-card-photo-generic"
          >
            <Icon
              name="user"
              size="xl2"
              as="span"
              role="img"
              aria-label={l10n('photo.alt')}
            />
          </GenericAvatar>
        )}
        {isEditable && isLoaded && (
          <AvatarEdit
            onClick={onEdit}
            aria-label={l10n('photo.edit')}
            data-test-id="edit-profile-photo"
          >
            <Icon name="camera" size="sm" className={`${EditCamera}`} />
          </AvatarEdit>
        )}
      </AvatarContainer>
      {isShareable && (
        <ShareContainer>
          <Link
            as={ShareButton}
            onClick={onShare}
            aria-label={l10n('share.label')}
            data-test-id="profile-card-share"
          >
            <Icon name="share" size="sm" />
          </Link>
        </ShareContainer>
      )}
      <Content showActions={showActions}>
        <HeadingText size="md" as={PrimaryText}>
          {displayName}
        </HeadingText>
        <SecondaryContent>
          <BodyText as={SecondaryText} data-test-id="profile-card-username">
            {handle}
          </BodyText>
          {displayLocation && (
            <BodyText as={SecondaryText} data-test-id="profile-card-location">
              {displayLocation}
            </BodyText>
          )}
        </SecondaryContent>
        {blurb && (
          <TertiaryContent>
            <BodyText as={SecondaryText} data-test-id="profile-card-about">
              {blurb}
            </BodyText>
          </TertiaryContent>
        )}
        {showAmount && (
          <AmountContainer data-test-id="profile-card-amount">
            <PPAmountField
              value={amount}
              currencyCode={currencyCode}
              currencyDecimal={currencyDecimal}
              currencySymbolMap={currencySymbolMap}
              getFormattedValue={formatAmount}
              fontFamily="PayPalSansBig-Regular, 'Helvetica Neue', Arial, sans-serif"
              inputProps={{
                readOnly: true,
                'aria-label': amountLabel,
              }}
              selectProps={{ disabled: true }}
            />
          </AmountContainer>
        )}
      </Content>
      {showActions && (
        <Footer>
          <Link
            as={Button}
            onClick={onSend}
            href={sendLink}
            data-test-id="profile-card-send"
          >
            {l10n('send')}
          </Link>
        </Footer>
      )}
    </Card>
  )
}

export default ProfileCard
